import React, { useState, useEffect, useMemo } from 'react';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

// --- DARK THEME CONSTANTS ---
const DARK_MODE_TEXT = '#E5E7EB'; // gray-200 for text
const DARK_MODE_GRID = '#4B5563'; // gray-600 for light grid lines

// --- IMPORTANT: Register all necessary Chart.js components ---
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
);

// Define chart options for a compact, clean look
// IMPORTANT: Use a function to dynamically set colors based on a theme flag if possible.
// For simplicity here, we'll rely on an external theme provider/hook to ensure these colors are right.
// Since we don't have the context here, we'll make a more robust `getCommonOptions` function.

const getCommonOptions = (isDarkMode) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: 'top',
            labels: {
                boxWidth: 10,
                padding: 15,
                color: isDarkMode ? DARK_MODE_TEXT : '#6B7280', // Legend text color
            }
        },
        title: { display: false },
        tooltip: {
            // Optional: Make tooltip background darker
            backgroundColor: isDarkMode ? 'rgba(31, 41, 55, 0.9)' : 'rgba(0,0,0,0.8)',
            titleColor: isDarkMode ? DARK_MODE_TEXT : '#fff',
            bodyColor: isDarkMode ? DARK_MODE_TEXT : '#fff',
        }
    },
    scales: {
        y: {
            grid: {
                drawOnChartArea: true,
                color: isDarkMode ? DARK_MODE_GRID : 'rgba(0, 0, 0, 0.1)', // Grid line color
            },
            ticks: { color: isDarkMode ? DARK_MODE_TEXT : '#6B7280' }
        },
        x: {
            grid: {
                drawOnChartArea: false,
                color: isDarkMode ? DARK_MODE_GRID : 'rgba(0, 0, 0, 0.1)',
            },
            ticks: { color: isDarkMode ? DARK_MODE_TEXT : '#6B7280' }
        }
    }
});


// --- UTILITY COMPONENTS & HOOKS ---

// 1. Loading Placeholder Component
// Added dark:bg-gray-800, dark:border-gray-700, dark:text-gray-400
export const LoadingPlaceholder = () => (
    <div className="flex items-center justify-center h-full bg-gray-50 dark:bg-gray-800 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
        <div className="flex flex-col items-center">
            <svg className="animate-spin h-5 w-5 text-blue-500 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading chart data...</p>
        </div>
    </div>
);

const STATUSES = ['Assigned', 'Started', 'InReview', 'Completed'];
const STATUS_COLORS = {
    'Completed': '#2ECC71', // Green
    'InReview': '#F39C12', // Orange/Yellow
    'Started': '#3498DB',  // Blue
    'Assigned': '#1ac0ccff', // Gray/Neutral
};


export const COLORS = [
    '#DC2626', '#3B82F6', '#10B981', '#F59E0B',
    '#3B82F6', '#10B981', '#F59E0B',
    '#4F46E5', '#9CA3AF',
]


// 2. Custom Hook for Independent Data Fetching
// (No Tailwind changes needed here)
export const useChartData = (dataFetcher, dependencies) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        setError(null);
        setData(null);

        // SIMULATE API CALL WITH RANDOM DELAY (500ms to 2000ms)
        // const delay = Math.floor(Math.random() * 1500) + 500;

        {
            try {
                const fetchedData = dataFetcher(); // Execute the data aggregation logic
                setData(fetchedData);
            } catch (err) {
                console.error("Data fetching error:", err);
                setError("Failed to load data.");
            } finally {
                setLoading(false);
            }
        }

        // return () => clearTimeout(timer); // Cleanup on dependency change or unmount
    }, dependencies);

    return { data, loading, error };
};


// --- CHART VISUALIZATIONS (Remain the same structure) ---

// --- WRAPPER COMPONENT ---
// Added dark:bg-gray-800, dark:shadow-2xl, dark:border-gray-700, dark:text-gray-200, dark:text-gray-400
export const ChartWrapper = ({ title, description, children, isDarkMode = false }) => (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-lg h-full transition duration-300 hover:shadow-xl dark:hover:shadow-2xl border border-gray-100 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2 flex items-center">
            {title.includes('Trend') ? '📈' : title.includes('Mastery') ? '🎯' : title.includes('Difficulty') ? '🧠' : '💡'} {title}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{description}</p>
        <div className="h-64">
            {children}
        </div>
    </div>
);



// Assuming Bar, commonOptions are imported correctly.

// --- 2. Simple Bar Chart (Usecase 3, 5, Chapter Counts) ---
export const SimpleBarChart = ({
    data,
    chartLabel = 'Data Value', // New prop for the dataset label (e.g., "Count", "Avg Score")
    chartColor = '#4C78A8',      // Default color
    yAxisMax,                   // Optional prop for setting max Y-axis value
    isDarkMode = false,         // New prop for theme
}) => {

    const commonOptions = getCommonOptions(isDarkMode);

    // The data is expected to be an array of objects: [{ label: "X-Axis Category", value: 123 }, ...]

    const chartData = {
        // X-Axis Labels: Maps the 'label' key from the aggregated data
        labels: data.map(d => d.label),

        datasets: [{
            // Dataset Label: Uses the passed chartLabel prop
            label: chartLabel,

            // Data Values: Maps the 'value' key from the aggregated data
            data: data.map(d => d.value),

            backgroundColor: chartColor,
            borderColor: chartColor,
            borderWidth: 1,
            borderRadius: 4,
        }],
    };

    const options = {
        // Ensure the chart scales properly within its container
        responsive: true,
        maintainAspectRatio: false,

        // Assuming commonOptions includes necessary global config
        ...commonOptions,

        scales: {
            ...commonOptions.scales,
            y: {
                ...commonOptions.scales.y,
                min: 0,
                // Optional: Set max value if provided
                max: yAxisMax,
                // Display the Y-axis label dynamically
                title: {
                    display: true,
                    text: chartLabel,
                    color: isDarkMode ? DARK_MODE_TEXT : '#6B7280'
                }
            }
        }
    };

    return <Bar data={chartData} options={options} />;
};

// --- 3. Simple Stacked Bar Chart ---
export const SimpleStackedBarChart = ({ data, title, isDarkMode = false }) => {
    // data is expected to be { labels: [...], datasets: [...] }

    const commonOptions = getCommonOptions(isDarkMode);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        ...commonOptions,
        plugins: {
            ...commonOptions.plugins,
            // Show the chart title if necessary
            title: {
                display: true,
                text: title,
                color: isDarkMode ? DARK_MODE_TEXT : '#6B7280',
            },
        },
        scales: {
            x: {
                ...commonOptions.scales.x,
                stacked: true, // CRITICAL: Enables stacking on the X-axis
                title: {
                    display: true,
                    text: 'Chapter',
                    color: isDarkMode ? DARK_MODE_TEXT : '#6B7280'
                }
            },
            y: {
                ...commonOptions.scales.y,
                stacked: true, // CRITICAL: Enables stacking on the Y-axis
                title: {
                    display: true,
                    text: 'Total Assignments Completed',
                    color: isDarkMode ? DARK_MODE_TEXT : '#6B7280'
                },
                min: 0,
            }
        },
    };

    // The component name must be <Bar> if using react-chartjs-2
    return <Bar data={data} options={options} />;
};
// Assume Doughnut and commonOptions (for plugins/fonts) are imported from your setup.
// Example colors for different difficulty levels (you should define these centrally)
const DIFFICULTY_COLORS = [
    '#2ECC71', // Green for Easy
    '#F39C12', // Yellow/Orange for Medium
    '#E74C3C', // Red for Hard
    '#3498DB', // Blue for any other category (e.g., 'Unknown')
    '#9B59B6', // Purple
];

export const MasteryDoughnutChart = ({ data, title, isDarkMode = false }) => {

    const commonOptions = getCommonOptions(isDarkMode);

    // Convert the simple data structure [{ label, value }] into the Chart.js format
    const chartData = {
        // Labels for the slices (e.g., "Easy", "Medium", "Hard")
        labels: data.map(d => d.label),

        datasets: [{
            // Only one dataset for a Doughnut chart
            label: 'Total Questions',

            // Data values for the slices (the counts)
            data: data.map(d => d.value),

            // Assign a color for each slice (difficulty level)
            backgroundColor: data.map((_, index) =>
                DIFFICULTY_COLORS[index % DIFFICULTY_COLORS.length]
            ),

            // Optional: Border properties for visual separation
            borderColor: isDarkMode ? '#1F2937' : 'white', // Darker border in dark mode
            borderWidth: 2,
            hoverOffset: 8, // Offset when hovering over a slice
        }],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false, // Ensures the chart fills the container

        // Merge common options (if defined)
        ...commonOptions,

        cutout: '70%', // Creates the 'doughnut' hole (e.g., 70% of the radius is cut out)

        plugins: {
            ...commonOptions.plugins,
            legend: {
                ...commonOptions.plugins.legend,
                position: 'right', // Place the legend on the right side
                labels: {
                    ...commonOptions.plugins.legend.labels,
                    color: isDarkMode ? DARK_MODE_TEXT : '#6B7280',
                }
            },
            title: {
                display: true,
                text: title || 'Distribution of Questions', // Use the title prop
                color: isDarkMode ? DARK_MODE_TEXT : '#6B7280',
                font: {
                    size: 16
                }
            },
            tooltip: {
                // Custom tooltips can show percentage, but default is usually sufficient
                ...commonOptions.plugins.tooltip
            },
        },
    };

    return <Doughnut data={chartData} options={options} />;
};


// --- 4. Difficulty vs. Performance (Multi-Axis Line Chart - Usecase 6) ---
export const DifficultyLineChart = ({ data, colors, isDarkMode = false }) => {

    const commonOptions = getCommonOptions(isDarkMode);

    const chartData = {
        labels: data.map(d => d.difficulty),
        datasets: [
            {
                label: 'Success Rate (%)',
                data: data.map(d => d.successRate),
                borderColor: colors.SUCCESS,
                backgroundColor: colors.SUCCESS + '30',
                yAxisID: 'ySuccess',
                tension: 0.3,
                borderWidth: 2,
                pointRadius: 5
            },
            {
                label: 'Avg Time (s)',
                data: data.map(d => d.avgTime),
                borderColor: colors.TIME,
                backgroundColor: colors.TIME + '30',
                yAxisID: 'yTime',
                tension: 0.3,
                borderWidth: 2,
                pointRadius: 5
            },
        ],
    };

    const options = {
        ...commonOptions,
        scales: {
            x: commonOptions.scales.x,
            ySuccess: {
                type: 'linear',
                display: true,
                position: 'left',
                min: 0,
                max: 100,
                title: {
                    display: true,
                    text: 'Success (%)',
                    color: colors.SUCCESS,
                    font: { size: 10 }
                },
                grid: { drawOnChartArea: true, color: isDarkMode ? DARK_MODE_GRID : 'rgba(0, 0, 0, 0.1)' },
                ticks: { color: colors.SUCCESS, callback: (value) => value + '%' }
            },
            yTime: {
                type: 'linear',
                display: true,
                position: 'right',
                min: 0,
                title: {
                    display: true,
                    text: 'Avg Time (s)',
                    color: colors.TIME,
                    font: { size: 10 }
                },
                grid: { drawOnChartArea: false },
                ticks: { color: colors.TIME, callback: (value) => value + 's' }
            },
        },
    };
    return <Line data={chartData} options={options} />;
};


// --- 5. Concept Topic Hotspot (Horizontal Bar Chart - Usecase 7) ---
export const HotspotBarChart = ({ data, isDarkMode = false }) => {

    const commonOptions = getCommonOptions(isDarkMode);

    const chartData = {
        labels: data.map(d => d.concept),
        datasets: [{
            label: 'Failure Rate (%)',
            data: data.map(d => d.failureRate),
            backgroundColor: data.map(d => d.color),
            borderColor: data.map(d => d.color),
            borderWidth: 1,
            borderRadius: 4,
        }],
    };

    const options = {
        ...commonOptions,
        indexAxis: 'y',
        scales: {
            x: {
                ...commonOptions.scales.x,
                min: 0,
                max: 100,
                title: {
                    display: true,
                    text: 'Failure Rate (%)',
                    color: isDarkMode ? DARK_MODE_TEXT : '#6B7280'
                }
            },
            y: commonOptions.scales.y
        }
    };

    return <Bar data={chartData} options={options} />;
};


// --- 6. Batch Performance Comparison (Grouped Bar Chart - Usecase 8) ---
export const BatchComparisonChart = ({ data, batchInfo, studentBatchId, colors, isDarkMode = false }) => {

    const commonOptions = getCommonOptions(isDarkMode);

    const chartData = {
        labels: data.map(d => d.name),
        datasets: batchInfo.map(batch => ({
            label: batch.name,
            data: data.map(d => d[batch.name] || 0),
            backgroundColor: batch.id === studentBatchId ? colors.BATCH1 : colors.AVERAGE,
            borderColor: batch.id === studentBatchId ? colors.BATCH1 : colors.AVERAGE,
            borderWidth: 1,
            borderRadius: 2,
        })),
    };

    const options = {
        ...commonOptions,
        scales: {
            y: {
                ...commonOptions.scales.y,
                min: 0,
                max: 100,
                title: {
                    display: true,
                    text: 'Average Score (%)',
                    color: isDarkMode ? DARK_MODE_TEXT : '#6B7280'
                }
            }
        },
        plugins: {
            ...commonOptions.plugins,
            legend: {
                ...commonOptions.plugins.legend,
                position: 'top',
            }
        }
    };

    return <Bar data={chartData} options={options} />;
};